/**
 * WindLegend
 * Displays wind speed color ramp, data source, and freshness badge.
 * Only visible when the wind layer is active and has data.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { WIND_COLOR_RAMP } from '@/config/windConfig';
import { useMapbox } from '@/contexts/MapboxContext';
import { useWindStore } from '@/stores/slices/useWindStore';

/** Speed unit definitions */
const UNITS = {
  'ms': { label: 'm/s', factor: 1 },
  'kt': { label: 'kt', factor: 1.94384 },
  'mph': { label: 'mph', factor: 2.23694 },
  'kmh': { label: 'km/h', factor: 3.6 },
} as const;

type UnitKey = keyof typeof UNITS;

const WIND_LAYER_ID = 'Weather - Wind Layer';

/** Build CSS gradient from color ramp */
function buildGradient(): string {
  const maxSpeed = WIND_COLOR_RAMP[WIND_COLOR_RAMP.length - 1][0];
  const stops = WIND_COLOR_RAMP.map(([speed, r, g, b]) => {
    const pct = (speed / maxSpeed) * 100;
    return `rgb(${r},${g},${b}) ${pct.toFixed(1)}%`;
  });
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

/** Format relative time */
function formatTimeAgo(timestamp: number): string {
  if (!timestamp) return '';
  const diffMs = Date.now() - timestamp;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function WindLegend() {
  const { map } = useMapbox();
  const { sourceName, lastFetchedAt, hasData } = useWindStore();
  const [unit, setUnit] = useState<UnitKey>('kt');
  const [timeAgo, setTimeAgo] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  // Check wind layer visibility from Mapbox (polls on repaint via idle event)
  useEffect(() => {
    const m = map.current;
    if (!m) return;

    const checkVisibility = () => {
      try {
        if (!m.getLayer(WIND_LAYER_ID)) {
          setIsVisible(false);
          return;
        }
        const vis = m.getLayoutProperty(WIND_LAYER_ID, 'visibility');
        setIsVisible(vis === 'visible');
      } catch {
        setIsVisible(false);
      }
    };

    checkVisibility();
    // Re-check whenever the map idles (covers toggle, style reload, etc.)
    m.on('idle', checkVisibility);
    return () => {
      m.off('idle', checkVisibility);
    };
  }, [map]);

  // Update "time ago" display every 30 seconds
  useEffect(() => {
    if (!lastFetchedAt) return;
    setTimeAgo(formatTimeAgo(lastFetchedAt));
    const interval = setInterval(() => setTimeAgo(formatTimeAgo(lastFetchedAt)), 30000);
    return () => clearInterval(interval);
  }, [lastFetchedAt]);

  const cycleUnit = useCallback(() => {
    const keys = Object.keys(UNITS) as UnitKey[];
    const idx = keys.indexOf(unit);
    setUnit(keys[(idx + 1) % keys.length]);
  }, [unit]);

  const gradient = useMemo(() => buildGradient(), []);

  const unitDef = UNITS[unit];
  const rampMax = WIND_COLOR_RAMP[WIND_COLOR_RAMP.length - 1][0];

  // Tick marks at reasonable intervals
  const ticks = useMemo(() => {
    const interval = rampMax <= 20 ? 5 : 10;
    const result: number[] = [0];
    for (let v = interval; v < rampMax; v += interval) result.push(v);
    result.push(rampMax);
    return result;
  }, [rampMax]);

  if (!hasData || !isVisible) return null;

  return (
    <div className="absolute bottom-14 left-3 z-10 pointer-events-auto select-none">
      <div className="backdrop-blur-xl bg-panel-surface/90 border border-panel-border rounded-lg px-3 py-2 shadow-lg min-w-[220px]">
        {/* Header: source + freshness */}
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] font-medium text-white/90 tracking-wide">
            {sourceName || 'Wind'}
          </span>
          {timeAgo && (
            <span className="text-[10px] text-white/50">
              {timeAgo}
            </span>
          )}
        </div>

        {/* Color ramp bar */}
        <div
          className="h-2.5 rounded-sm w-full cursor-pointer"
          style={{ background: gradient }}
          onClick={cycleUnit}
          title={`Click to change units (${unitDef.label})`}
        />

        {/* Tick labels */}
        <div className="flex justify-between mt-0.5">
          {ticks.map((speed) => (
            <span key={speed} className="text-[9px] text-white/60 tabular-nums">
              {Math.round(speed * unitDef.factor)}
            </span>
          ))}
        </div>

        {/* Unit label */}
        <div className="flex items-center justify-between mt-0.5">
          <span className="text-[9px] text-white/40">Wind Speed</span>
          <button
            onClick={cycleUnit}
            className="text-[9px] text-blue-400/80 hover:text-blue-300 transition-colors cursor-pointer"
          >
            {unitDef.label}
          </button>
        </div>
      </div>
    </div>
  );
}

export type WindLegendProps = Record<string, never>;
